import axios, { AxiosResponse } from 'axios';
import { ApiResponse, Player, Tournament, Club } from '../types';
import { getCookie, setCookie, deleteCookie } from '../utils/cookies';

const BASE_URL = 'https://mafiaworldtour.org';
const COOKIE_DOMAIN = '.mafiaworldtour.org'; // Legacy system compatibility

class ApiService {
  private token: string | null = null;

  constructor() {
    // Check both localStorage and cookies for token (for legacy compatibility)
    this.token = localStorage.getItem('token') || getCookie('token');

    // If token found in cookies but not in localStorage, sync it
    if (!localStorage.getItem('token') && this.token) {
      localStorage.setItem('token', this.token);
      console.log('[API] Token synced from cookies to localStorage');
    }

    // If token found in localStorage but not in cookies, sync it to cookies with proper domain
    if (localStorage.getItem('token') && !getCookie('token')) {
      setCookie('token', localStorage.getItem('token')!, 30, '/', COOKIE_DOMAIN);
      console.log('[API] Token synced from localStorage to cookies');
    }

    this.setupInterceptors();
  }

  private setupInterceptors() {
    axios.interceptors.request.use((config) => {
      // Check if token exists before making request
      const token = this.token || localStorage.getItem('token');
      if (!token) {
        console.warn('[API] No authentication token found');
        // Don't reject - let the request proceed and server will return 401/403
      } else {
        this.token = token;
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add CORS headers
      config.headers['Content-Type'] = 'application/json';
      config.headers['Accept'] = 'application/json';
      // Enable credentials for CORS
      config.withCredentials = false; // Set to false to avoid preflight issues
      return config;
    });

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Log detailed error information for debugging
        if (error.response) {
          console.error('[API] Response error:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            url: error.config?.url
          });
        } else if (error.request) {
          console.error('[API] Request error (no response):', {
            message: error.message,
            url: error.config?.url
          });
        } else {
          console.error('[API] Error:', error.message);
        }
        
        // Handle both 401 (Unauthorized) and 403 (Forbidden) as auth errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.warn('[API] Authentication error:', error.response?.status);
          this.logout();
          // Redirect to login page
          window.location.href = '/';
          // Show user-friendly message
          alert('Your session has expired. Please log in again.');
        }
        
        // Log validation errors for easier debugging
        if (error.response?.status === 422) {
          console.warn('[API] Validation error (422):', error.response?.data);
        }
        
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    this.token = token;
    // Save to both localStorage (new app) and cookies (legacy compatibility)
    localStorage.setItem('token', token);
    setCookie('token', token, 30, '/', COOKIE_DOMAIN); // 30 days, same as legacy
    console.log('[API] Token saved to localStorage and cookies');
  }

  logout() {
    this.token = null;
    // Remove from both localStorage (new app) and cookies (legacy compatibility)
    localStorage.removeItem('token');
    deleteCookie('token', '/', COOKIE_DOMAIN); // Use same domain as legacy

    // Also clean up cookies for user data (legacy compatibility)
    deleteCookie('myID', '/', COOKIE_DOMAIN);
    deleteCookie('myName', '/', COOKIE_DOMAIN);
    deleteCookie('myAvatar', '/', COOKIE_DOMAIN);
    deleteCookie('l', '/', COOKIE_DOMAIN); // language

    // Clean up localStorage
    localStorage.removeItem('myID');
    localStorage.removeItem('myName');
    localStorage.removeItem('myAvatar');
    localStorage.removeItem('reGameState');

    console.log('[API] Logged out - cleared localStorage and cookies');
  }

  async login(email: string, password: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(`${BASE_URL}/api/auth/login`, {
        email,
        password,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTournaments(): Promise<ApiResponse<Tournament[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Tournament[]>> = await axios.get(
        `${BASE_URL}/api/tournament/closest`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAllTournaments(): Promise<ApiResponse<Tournament[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Tournament[]>> = await axios.get(
        `${BASE_URL}/api/tournament/opened`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTournamentSeats(tournamentId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/tournament/seats`,
        { tournament_id: tournamentId }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async loadProtocol(params: {
    tournament_id: number;
    part_number: number;
    table_number: number;
    session_number: number;
  }): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/protocol/load`,
        params
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveProtocol(result: any): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/protocol/save`,
        result
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async loadClubs(): Promise<ApiResponse<Club[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Club[]>> = await axios.get(
        `${BASE_URL}/api/club/load`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createClubEvent(params: {
    club_id: number;
    title: string;
    start_date: string;
    description: string;
    limit: number;
    timeslots: string[];
  }): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/post/event`,
        params
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getClubGames(eventId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/get/games`,
        { eid: eventId }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveClubGame(clubId: number, result: any): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/${clubId}/save`,
        result
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async searchUsers(searchString: string, clubId?: number): Promise<ApiResponse<Player[]>> {
    try {
      const response: AxiosResponse<ApiResponse<Player[]>> = await axios.post(
        `${BASE_URL}/api/user/search`,
        {
          search_string: searchString,
          club_id: clubId || '',
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async pushStream(params: {
    tid?: number;
    cid?: number;
    taid: number;
    method: string;
    info: any;
  }): Promise<ApiResponse> {
    try {
      const url = params.tid 
        ? `${BASE_URL}/api/stream/push`
        : `${BASE_URL}/api/club-stream/push`;
      
      const response: AxiosResponse<ApiResponse> = await axios.post(url, params, {
        timeout: 4000, // 4 second timeout for stream sync
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getPenalties(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.get(
        `${BASE_URL}/api/penalties`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateParticipantStatus(eventId: number, participantId: number, status: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/post/participant`,
        {
          eid: eventId,
          participant: participantId,
          status: status,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteParticipant(eventId: number, participantId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/delete/participant`,
        {
          eid: eventId,
          participant: participantId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteGame(gameId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/delete/game`,
        {
          gid: gameId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async closeEvent(eventId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/post/eventclose`,
        {
          eid: eventId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async openEvent(eventId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/post/eventopen`,
        {
          eid: eventId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteEvent(eventId: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/delete/event`,
        {
          eid: eventId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateEventRanking(eventId: number, ranked: number): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${BASE_URL}/api/club/post/eventranked`,
        {
          eid: eventId,
          eranked: ranked,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const apiService = new ApiService();